// ─────────────────────────────────────────────────────────────────────────────
// Shared Task Utilities
// Used by: TaskBoard.js, Task.js (and any other task-related page)
// ─────────────────────────────────────────────────────────────────────────────

// ── Constants ────────────────────────────────────────────────────────────────

export const statusCards = [
    { label: "All", color: "#7b6ef6" },
    { label: "Pending", color: "#a259e6" },
    { label: "Planned", color: "#4fc3f7" },
    { label: "WIP", color: "#ff9800" },
    { label: "Completed", color: "#4caf50" },
    { label: "Revalidate", color: "#f48fb1" },
    { label: "Delegated", color: "#ffb300" },
    { label: "Reopen", color: "#90a4ae" },
];

export const priorityFilterOptions = [
    { value: "urgent", label: "Urgent" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
];

export const ageingFilterOptions = [
    { value: "lt1d", label: "Under 1 day", minMinutes: 0, maxMinutes: 1440 },
    { value: "1to3d", label: "1 - 3 days", minMinutes: 1440, maxMinutes: 4320 },
    { value: "3to7d", label: "3 - 7 days", minMinutes: 4320, maxMinutes: 10080 },
    { value: "7to30d", label: "7 - 30 days", minMinutes: 10080, maxMinutes: 43200 },
    { value: "gt30d", label: "30+ days", minMinutes: 43200, maxMinutes: Number.POSITIVE_INFINITY },
];

/**
 * Formats minutes into a human-readable ageing string (e.g. "3d 4h").
 * Uses a priority-based display to reduce noise.
 */
export function formatAgeing(minutes) {
    if (minutes == null || isNaN(minutes) || minutes <= 0) return "-";

    const MINUTES_IN_HOUR = 60;
    const MINUTES_IN_DAY = 60 * 24;
    const MINUTES_IN_MONTH = MINUTES_IN_DAY * 30; // approx
    const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365; // approx

    let remaining = Math.max(0, minutes);

    const years = Math.floor(remaining / MINUTES_IN_YEAR);
    remaining %= MINUTES_IN_YEAR;

    const months = Math.floor(remaining / MINUTES_IN_MONTH);
    remaining %= MINUTES_IN_MONTH;

    const days = Math.floor(remaining / MINUTES_IN_DAY);
    remaining %= MINUTES_IN_DAY;

    const hours = Math.floor(remaining / MINUTES_IN_HOUR);
    remaining %= MINUTES_IN_HOUR;

    const mins = Math.floor(remaining);

    const result = [];

    if (years) {
        result.push(`${years}y`);
        if (months) result.push(`${months}mon`);
        return result.join(" ");
    }

    if (months) {
        result.push(`${months}mon`);
        if (days) result.push(`${days}d`);
        return result.join(" ");
    }

    if (days) {
        result.push(`${days}d`);
        if (hours) result.push(`${hours}h`);
        return result.join(" ");
    }

    if (hours) {
        result.push(`${hours}h`);
        if (mins) result.push(`${mins}min`);
        return result.join(" ");
    }

    return mins > 0 ? `${mins}min` : "-";
}

export const badgeClassLookup = {
    pending: "Pending",
    planned: "Planned",
    wip: "WIP",
    "in progress": "WIP",
    "in-progress": "WIP",
    hold: "Pending",
    revalidate: "Revalidate",
    delegated: "Delegated",
    reopen: "Reopen",
    completed: "Completed",
    mixed: "Mixed",
};

export const MAX_STATUS_ATTACHMENTS = 5;

/** Ticket / reference id for display in task tables (several API shapes). */
export function resolveTaskTicketId(task) {
    if (!task) return "";
    const raw =
        task.ticketId ??
        task.ticket_id ??
        (Array.isArray(task.ticket_ids) ? task.ticket_ids[0] : null) ??
        task.ticketNumber ??
        task.ticket_number;
    if (raw == null || raw === "") return "";
    return String(raw).trim();
}

/** Project label for task tables (nested `project` or flat name fields). */
export function resolveTaskProjectName(task) {
    if (!task) return "";
    const name =
        task.project?.name ??
        task.project_name ??
        task.projectName ??
        (typeof task.project === "string" ? task.project : "");
    if (name == null || name === "") return "";
    return String(name).trim();
}

// ── Date / Time Helpers ───────────────────────────────────────────────────────

/**
 * Normalises any date-like value (Date, number, string) into a Date object.
 * For day-only strings (YYYY-MM-DD, DD/MM/YYYY, etc.) the time is set to
 * 23:59:59.999 so that "today" tasks are still counted as within-day.
 * Returns null if the value cannot be parsed.
 */
export function normalizeToDate(value) {
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value === "number" && Number.isFinite(value)) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const raw = String(value || "").trim();
    if (!raw) return null;
    const isoReady = raw.includes("T") ? raw : raw.replace(" ", "T");
    const d = new Date(isoReady);
    if (isNaN(d.getTime())) return null;

    // Set end-of-day for date-only strings so "today" tasks aren't missed
    const isDayOnly =
        /^(\d{4}-\d{2}-\d{2})$/.test(raw) ||
        /^(\d{2}[-/]\d{2}[-/]\d{4})$/.test(raw) ||
        (!/[T\s]\d{2}:\d{2}/.test(raw) && raw.length <= 10);
    if (isDayOnly) d.setHours(23, 59, 59, 999);
    return d;
}

/**
 * Returns the best available "start" date field from a task object,
 * trying several common field name conventions.
 */
export const resolveTaskStartDate = (task) =>
    task?.start_date ||
    task?.startDate ||
    task?.created_at ||
    task?.createdAt ||
    task?.due_date ||
    task?.dueDate ||
    null;

/**
 * Whether a task status represents a completed state.
 */
export function isCompletedStatus(status) {
    const normalized = String(status ?? "")
        .trim()
        .toLowerCase();
    return normalized === "completed" || normalized === "complete" || normalized === "done";
}

/**
 * Resolves the completion Date object for a task (only when the task is in a
 * "completed" state).  Returns null for any other status.
 */
export function resolveTaskCompletionDate(task) {
    if (!task) return null;
    const status = (
        task?.status ||
        task?.statusForCurrentUser ||
        task?.originalStatus ||
        ""
    )
        .toString()
        .trim()
        .toLowerCase();
    if (!isCompletedStatus(status)) return null;

    const candidates = [
        task?.updated_at,
        task?.updatedAt,
        task?.status_updated_at,
        task?.statusUpdatedAt,
        task?.completed_at,
        task?.completedAt,
    ];
    for (const value of candidates) {
        const parsed = normalizeToDate(value);
        if (parsed) return parsed;
    }
    return null;
}

/**
 * Returns the completion timestamp (ms) for ageing calculations.
 * Handles both individual tasks and group-aggregated task entries.
 */
export function resolveTaskCompletionTimestamp(task) {
    if (!task) return null;
    const status = (task?.status || task?.statusForCurrentUser || "")
        .toString()
        .trim()
        .toLowerCase();

    // Group-aggregated entry: only use the pre-computed timestamp when fully completed
    if (
        typeof task.ageingCompletionTimestamp === "number" &&
        !Number.isNaN(task.ageingCompletionTimestamp)
    ) {
        return status === "completed" || status === "complete" || status === "done"
            ? task.ageingCompletionTimestamp
            : null;
    }

    const completionDate = resolveTaskCompletionDate(task);
    return completionDate ? completionDate.getTime() : null;
}

/**
 * Returns the elapsed time (ms) between the anchor date and either now or a
 * cutoff timestamp — whichever is earlier.  Returns 0 (not negative) if the
 * anchor is in the future.
 */
export function calculateDelayMs(targetDateValue, cutoffDateValue = null) {
    const anchor = normalizeToDate(targetDateValue);
    if (!anchor) return null;
    let comparisonTimestamp = Date.now();
    if (cutoffDateValue !== null && cutoffDateValue !== undefined) {
        const cutoffDate = normalizeToDate(cutoffDateValue);
        if (cutoffDate) {
            comparisonTimestamp = Math.min(comparisonTimestamp, cutoffDate.getTime());
        }
    }
    const diff = comparisonTimestamp - anchor.getTime();
    return diff <= 0 ? 0 : diff;
}

/**
 * Returns a human-readable ageing string (e.g. "3d 4h", "2mo", "1y 3mo")
 * for display in the Ageing column.
 */
export function calculateAgeingDisplay(targetDateValue, cutoffDateValue = null) {
    const delayMs = calculateDelayMs(targetDateValue, cutoffDateValue);
    if (delayMs === null) return "";
    if (delayMs <= 0) return "-";
    const seconds = Math.floor(delayMs / 1000);
    if (seconds <= 0) return "-";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        const remS = seconds % 60;
        return remS > 0 ? `${minutes}min ${remS}s` : `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        const remM = minutes % 60;
        return remM > 0 ? `${hours}h ${remM}min` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    if (days < 30) {
        const remH = hours % 24;
        return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
        const remD = days % 30;
        return remD > 0 ? `${months}mon ${remD}d` : `${months}mon`;
    }
    const years = Math.floor(months / 12);
    const remMo = months % 12;
    return remMo > 0 ? `${years}y ${remMo}mon` : `${years}y`;
}

// ── Formatting Helpers ────────────────────────────────────────────────────────

/** Standardizing formatter for a Date object into DD/MM/YYYY. */
export function formatToDDMMYYYY(d) {
    if (!d || isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

/**
 * Formats a date string to "DD/MM/YYYY" using the standardized logic.
 */
export function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return formatToDDMMYYYY(d);
}

/**
 * Formats a byte count to a human-readable file size string (e.g. "1.4 MB").
 */
export function formatFileSize(bytes) {
    if (!bytes || Number.isNaN(Number(bytes)) || Number(bytes) <= 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    let value = Number(bytes);
    let index = 0;
    while (value >= 1024 && index < units.length - 1) {
        value /= 1024;
        index += 1;
    }
    const rounded =
        value >= 100 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
    return `${rounded} ${units[index]}`;
}

// ── Group Aggregation (Task.js) ───────────────────────────────────────────────

/**
 * Collapses a flat list of task rows (which may share a group_id) into
 * aggregated group entries suitable for table display.
 */
export function aggregateTasksByGroup(tasks) {
    if (!Array.isArray(tasks) || tasks.length === 0) return [];
    const groupMap = new Map();
    const order = [];

    tasks.forEach((task) => {
        const fallbackId =
            task?.id ?? `temp-${Math.random().toString(36).slice(2)}`;
        const hasGroup =
            task?.group_id !== null && task?.group_id !== undefined;
        const key = hasGroup ? `group-${task.group_id}` : `task-${fallbackId}`;

        if (!groupMap.has(key)) {
            groupMap.set(key, {
                ...task,
                primaryTaskId: task?.id ?? null,
                assignees: [],
                tasks: [],
                allTaskIds: [],
                start_date: task?.start_date || null,
                ageingCompletionTimestamp: null,
            });
            order.push(key);
        }

        const entry = groupMap.get(key);
        const completionDate = resolveTaskCompletionDate(task);
        const completionTimestamp = completionDate ? completionDate.getTime() : null;
        const fullName = `${task?.FirstName || ""} ${task?.LastName || ""}`
            .replace(/\s+/g, " ")
            .trim();

        entry.assignees.push({
            taskId: task?.id ?? null,
            userId: task?.assigned_to ?? null,
            name: fullName || "Unassigned",
            email: task?.email || "",
            status: task?.status || "",
            priority: task?.priority || "",
            due_date: task?.due_date || null,
            created_at: task?.created_at || null,
            start_date: task?.start_date || null,
            ticket_id: resolveTaskTicketId(task),
            ageingCompletionTimestamp: completionTimestamp,
        });
        entry.allTaskIds.push(task?.id ?? null);
        entry.tasks.push(task);

        if (completionTimestamp !== null) {
            entry.ageingCompletionTimestamp =
                entry.ageingCompletionTimestamp !== null
                    ? Math.max(entry.ageingCompletionTimestamp, completionTimestamp)
                    : completionTimestamp;
        }
    });

    return order.map((key) => {
        const entry = groupMap.get(key);
        const assignees = entry.assignees || [];
        const total = assignees.length;
        const statusSet = new Set(
            assignees
                .map((a) => (a.status || "").trim().toLowerCase())
                .filter(Boolean)
        );
        const aggregatedStatus =
            statusSet.size <= 1
                ? assignees[0]?.status || entry.status || "Pending"
                : "Mixed";

        return {
            ...entry,
            id: entry.primaryTaskId ?? entry.id,
            hasMultipleAssignees: total > 1,
            status: aggregatedStatus,
            assigneeSummary: {
                total,
                names: assignees.map((a) => a.name),
            },
        };
    });
}
